// Task #3085 — Presentational helpers for legacy CSV-imported bookings.
//
// Legacy imports (source='legacy_csv_import') are old-platform bookings
// with no Stripe artifact: they were paid externally and live in the
// new trips table for visibility only. The api-server Stripe paths are
// guarded by `guardLegacyImportStripeCall` (see
// api-server/src/lib/legacy-import-stripe-guard.ts) so the *backend*
// already refuses to charge / refund / payout these rows. This module
// keeps the *frontend* honest: any in-app action whose accept-side path
// touches Stripe must be hidden for legacy rows so the host never sees a
// button that the server would refuse, and money/payout chips render an
// "external" tone instead of the misleading "pending" we get from
// payment_status=0.
//
// Keep this helper TINY and dependency-free. `isLegacyImportTrip` reads only
// `trip.source` and stays CSV-only — it drives the earnings fallback below, where
// `price_carowner` is a CSV-importer-specific column. Action-gating, however,
// uses the broader `isImportedTrip` (Task #5101), which also covers bulk-migrated
// and native-historic DriveShare bookings. We still do NOT widen to the older
// `Website`/`web`/`API|N` sources: those carry real Stripe artifacts and are not
// guarded server-side, so conflating them here would hide legitimate actions.

import { DEFAULT_TRIP_CURRENCY, resolveTripCurrency } from "@workspace/booking-rules";
import type { ImportedMoneyContext, HistoricalSettlement } from "./admin-api/types";

/** Rolling-deploy fallback: only persisted origin selects a legacy schema. */
export function resolveImportedMoneyContext(
  trip: Parameters<typeof isImportedTrip>[0] & { status?: string | null; completed_at?: string | null; payout_status?: number | null },
  context?: ImportedMoneyContext,
  settlement?: HistoricalSettlement | null,
): ImportedMoneyContext | undefined {
  if (!isImportedTrip(trip)) return undefined;
  if (context) return context;
  const origin = trip?.platform_origin?.trim().toLowerCase();
  const mode = origin === "ds" || origin === "driveshare" ? "driveshare"
    : origin === "bac" || origin === "bookaclassic" ? "bac" : "unknown";
  // Without the new API context we cannot prove payout-link reads succeeded.
  return { mode, payout: "unavailable", historical: false,
    settlement: settlement ?? { available: false, movements: [] } };
}

export function describeImportedPayout(context: ImportedMoneyContext): string {
  if (context.payout === "unavailable") return "Payout records unavailable";
  if (context.payout === "recorded") return "Payout recorded";
  if (context.payout === "auto_stamped") return context.historical
    ? "Settled off-platform (historical import flag; no payout record)"
    : "Auto-stamped settled — no payout record";
  return "No payout recorded";
}

export const LEGACY_CSV_IMPORT_SOURCE = "legacy_csv_import" as const;

// Task #5524 — on the old platform the renter's service fee was a
// front-end-only 15% markup on the rental fee that was never persisted.
// Imported (off-platform) bookings therefore land here with `service_fee`
// 0/absent and no committed total, so the renter overview understates the
// price (rental only). This is the single source of that legacy rate; the
// renter trip-detail breakdown reuses it to surface a service fee and a
// rental x 1.15 total for imported rows that carry no stored service fee.
export const LEGACY_SERVICE_FEE_RATE = 0.15;

/**
 * The legacy renter service fee = 15% of the rental fee, rounded to 2dp to
 * reconcile with the rest of the money breakdown. Returns 0 for a missing
 * or non-positive rental so callers can use it unconditionally.
 */
export function legacyServiceFee(rentalFee: number | null | undefined): number {
  const r = Number(rentalFee ?? 0);
  if (!Number.isFinite(r) || r <= 0) return 0;
  return Math.round(r * LEGACY_SERVICE_FEE_RATE * 100) / 100;
}

// Task #5101 — native-DriveShare platform origins (mirror of the api-server
// leaf guard legacy-import-stripe-guard.ts).
const IMPORTED_PLATFORM_ORIGINS = new Set(["driveshare", "ds"]);

export function isLegacyImportTrip(
  trip: { source?: string | null } | null | undefined,
): boolean {
  return !!trip && trip.source === LEGACY_CSV_IMPORT_SOURCE;
}

/**
 * Task #5101 — a booking that was settled OFF the new BookAClassic platform and
 * therefore has NO Stripe artifact to cancel / refund / pay out against. Three
 * populations qualify, mirroring the server-side guards
 * (api-server: legacy-import-stripe-guard.ts `isImportedTrip` +
 * shared-cancellation `isImportedOriginTrip` + owner-payout):
 *   • legacy CSV imports         → source === 'legacy_csv_import'
 *   • bulk-migrated DriveShare    → source_id IS NOT NULL (the legacy DB pkey)
 *   • native-historic DriveShare  → platform_origin in {driveshare, ds}
 *
 * Keyed on `source_id`, not the mutable `source` string, because migration 3102's
 * backfill clobbered `source` to 'request'/'direct_booking'/'concierge' on
 * migrated rows (3104 repairs it) — a `source`-only check would render the Cancel
 * button on a booking the server would refuse to cancel. Genuine native bookings
 * have source_id NULL and a BAC platform_origin, so they are never matched. Use
 * this (not the CSV-only `isLegacyImportTrip`) to gate any in-app action whose
 * accept-side path routes through Stripe.
 */
export function isImportedTrip(
  trip:
    | { source?: string | null; source_id?: number | string | null; platform_origin?: string | null }
    | null
    | undefined,
): boolean {
  if (!trip) return false;
  if (isLegacyImportTrip(trip)) return true;
  if (trip.source_id != null && String(trip.source_id).trim() !== "") return true;
  const po =
    typeof trip.platform_origin === "string" ? trip.platform_origin.trim().toLowerCase() : "";
  return po !== "" && IMPORTED_PLATFORM_ORIGINS.has(po);
}

/**
 * Task #5647 — an imported booking that belongs to a BookAClassic (non-US)
 * market. These migrated rows never stored a genuine renter service fee: the
 * stored `service_fee` is the legacy BAC platform cut counted twice (40/50/60%),
 * not a 15% renter fee, so the renter overview must override it with
 * BookAClassic's current 15% rate. US DriveShare imports are genuine (USD, ~10%
 * stored) and out of scope, so we gate on a NON-USD currency signal resolved
 * through the same `resolveTripCurrency` rule the rest of the UI uses (no new
 * "US market" definition). Display-only — this never gates a Stripe action, so
 * it must NOT replace `isImportedTrip` at the action-gating callsites.
 */
export function isImportedBacMarketTrip(
  trip:
    | {
        source?: string | null;
        source_id?: number | string | null;
        platform_origin?: string | null;
        billing_currency_code?: string | null;
        local_currency_code?: string | null;
      }
    | null
    | undefined,
): boolean {
  if (!isImportedTrip(trip)) return false;
  return resolveTripCurrency(trip) !== DEFAULT_TRIP_CURRENCY;
}

/**
 * Task #5652 — single decision point for the renter-facing legacy service-fee
 * override, shared by the on-page Payment breakdown and every renter PDF
 * (trip PDF + booking receipt) so a downloaded document can never disagree
 * with the screen it mirrors. Two populations force the 15% fee:
 *   • imported BAC-market (non-US) rows — the stored `service_fee` is the
 *     legacy platform cut counted twice, never a renter fee (Task #5647);
 *   • any other imported row with NO stored service fee — the old platform's
 *     15% markup was front-end-only and never persisted (Task #5524).
 * US DriveShare imports with a genuine stored fee and all normal bookings
 * return false and keep their committed values. Display-only — never use
 * this to gate a Stripe action.
 */
export function shouldUseLegacyServiceFee(
  trip: Parameters<typeof isImportedBacMarketTrip>[0],
  rentalFee: number | null | undefined,
  storedServiceFee: number | null | undefined,
): boolean {
  const rental = Number(rentalFee ?? 0);
  if (!Number.isFinite(rental) || rental <= 0) return false;
  if (isImportedBacMarketTrip(trip)) return true;
  return isImportedTrip(trip) && Number(storedServiceFee ?? 0) <= 0;
}

/**
 * Task #5914 — the single source of truth for "the total the renter receipt
 * (and trip PDF) will render", shared by the trip-detail Download-receipt
 * button's visibility gate AND the generator inside `handleDownloadReceipt`,
 * so the gate and the PDF can never disagree.
 *
 * Legacy/imported bookings (canonical victim trip 1198092 / 198092, BAC/CAD)
 * left `payment_due` / `total_price` NULL even though the renter paid and the
 * fee components (`rental_fee`, `service_fee`) are populated. Gating the button
 * on the raw NULL committed total hid the receipt entirely. The total is
 * derived as:
 *   • when the legacy service-fee override applies (#5652 — imported BAC-market
 *     rows and imported rows with no stored fee), re-anchor to the visible
 *     line items (rental x 1.15 for the typical row) because the stored total is
 *     the doubled / missing-fee figure and must be ignored;
 *   • otherwise prefer the committed total when it is positive (native
 *     bookings — byte-identical to the previous behaviour);
 *   • otherwise fall back to the sum of the visible line items so an imported
 *     row with a NULL committed total still receipts its real total.
 * Returns 0 when nothing positive can be derived, so the button stays hidden
 * and the generator never emits a "$0" receipt.
 */
export function computeReceiptTotal(input: {
  trip: Parameters<typeof shouldUseLegacyServiceFee>[0];
  rentalFee: number | null | undefined;
  storedServiceFee: number | null | undefined;
  deliveryFee: number | null | undefined;
  insuranceFee: number | null | undefined;
  climateFee: number | null | undefined;
  /**
   * Task #8163 — applied discounts (Pro Owner + coupon) persisted on the
   * pricing snapshot. Subtracted from the line-items fallback so a receipt
   * that itemizes negative discount rows reconciles to its total. The
   * committed-total branch is already net of discounts.
   */
  discountTotal?: number | null | undefined;
  committedTotal: number | null | undefined;
}): number {
  const num = (v: number | null | undefined): number => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };
  const rental = num(input.rentalFee);
  const storedService = num(input.storedServiceFee);
  const useLegacy = shouldUseLegacyServiceFee(input.trip, rental, storedService);
  const service = useLegacy ? legacyServiceFee(rental) : storedService;
  const lineItemsSum =
    Math.round(
      (rental +
        num(input.deliveryFee) +
        num(input.insuranceFee) +
        num(input.climateFee) +
        service -
        num(input.discountTotal)) *
        100,
    ) / 100;
  if (!useLegacy) {
    const committed = num(input.committedTotal);
    if (committed > 0) return committed;
  }
  return lineItemsSum > 0 ? lineItemsSum : 0;
}

// Task #6190 — go-live cutoff for the imported-payout DISPLAY truth. The
// legacy import (and the ongoing `bac-legacy-auto` writer) auto-stamps
// completed imports `payout_status = 2` with NO settlement artifact, so admin
// + host surfaces read them as "paid / settled off-platform" even when no
// payout was ever recorded. The #6040 masked-paid data fix carved out the
// exact cohort to correct: imported completions ON/AFTER go-live
// (completed_at >= 2026-06-25) with no artifact. Pre-go-live imports (~16k)
// are the intended historical backlog and STAY "Settled off-platform".
//
// This is a DELIBERATELY distinct literal from the api-server GO_LIVE_CUTOFF_ISO
// (REVIEW_REQUEST_CUTOVER_ISO = 2026-06-18): the payout revert used 2026-06-25,
// so the display must gate on that exact date to match the corrected cohort.
export const IMPORTED_PAYOUT_GO_LIVE_CUTOFF_ISO = "2026-06-25T00:00:00Z";

/**
 * Task #6190 — whether an imported booking's completion falls on/after the
 * go-live payout cutoff. A missing / unparseable completion date is treated as
 * pre-go-live (the historical backlog default), so it never flips a row to the
 * honest "no payout recorded" state on a date we cannot trust.
 */
export function isPostGoLiveCompletion(completedAt: string | null | undefined): boolean {
  if (!completedAt) return false;
  const ms = Date.parse(completedAt);
  if (!Number.isFinite(ms)) return false;
  return ms >= Date.parse(IMPORTED_PAYOUT_GO_LIVE_CUTOFF_ISO);
}

/**
 * Task #6190 — the single decision for "should this IMPORTED booking's host
 * payout read as genuinely settled off-platform?". Callers MUST only invoke it
 * for imported trips (native bookings keep their existing on-platform logic).
 *   • pre-go-live completion (or no completion date) → true (historical backlog)
 *   • post-go-live completion WITH a settlement artifact → true
 *   • post-go-live completion with NO artifact → false → the #6040 masked-paid
 *     cohort: show an honest "no payout recorded" state, never "paid/settled".
 * A "settlement artifact" is a recorded external / primary op=3 payout billing
 * row or a completed payout link — the same discriminator the api-server
 * masked-paid sweep + external-payout-guard.ts use. Display-only: this never
 * changes any money state.
 */
export function isImportedPayoutSettled(input: {
  completedAt: string | null | undefined;
  hasSettlementArtifact: boolean;
}): boolean {
  if (!isPostGoLiveCompletion(input.completedAt)) return true;
  return input.hasSettlementArtifact;
}

/**
 * Owner earnings for a list/detail card, with the legacy fallback.
 * Legacy imports populate `price_carowner` (the old-platform host
 * earnings column) but leave `owner_earn` null, so a naive read of
 * `owner_earn` renders a blank tile. We prefer `owner_earn` for any
 * non-legacy row so we never accidentally surface `price_carowner` on
 * a new-platform trip whose earnings should come from the committed
 * trip_suggestion or trip root.
 */
export function getDisplayOwnerEarn(
  trip: { source?: string | null; owner_earn?: number | null; price_carowner?: number | null } | null | undefined,
): number | null {
  if (!trip) return null;
  if (trip.owner_earn != null) return Number(trip.owner_earn);
  if (isLegacyImportTrip(trip) && trip.price_carowner != null) {
    return Number(trip.price_carowner);
  }
  return null;
}
